import { useCallback } from 'react';
import { Formik, Form } from 'formik';
import { useTranslation } from '@klaviyo-mkt/gatsby-components/hooks/useTranslation';
import * as Yup from 'yup';

import Button from '@klv/atoms/button';
import Icon from '@klv/atoms/icon';
import { RadioGroup } from '@klv/molecules/form-field/radio-group';
import TextareaInput from '@klv/molecules/form-field/textarea';

import CheckboxInput from '../form-field/checkbox';
import TextInput from '../form-field/text';

import { useStoredVotes, useStoredFeedback } from './hooks';

import {
  submitFeedback,
  INITIAL_VALUES,
  PUBLIC_GRAPHQL_ENDPOINT,
  EXCLUDED_ARTICLES,
} from './utils';

import * as styles from './styles/articleVoteForm.module.scss';

import type { ReactNode } from 'react';
import type { IArticleVoteForm, IFeedbackFormValues } from './types';
import { generateClassName } from '@klv/shared/utils/functions';

const ArticleVoteForm = ({
  articleId,
  backgroundColor = 'light',
  category = '',
  className,
  headline,
  locale,
  pageType = 'hc',
  ...props
}: IArticleVoteForm) => {
  const { t } = useTranslation();
  
  const VALIDATION_SCHEMA = Yup.object({
    improvements: Yup.string(),
    allowContact: Yup.boolean(),
    email: Yup.string().email().when('allowContact', (allowContact, schema) => {
      if (allowContact[0]) {
        return schema.required(t('molecules_articleVoteForm_form_errorLabel'));
      }
  
      return schema;
    }),
    reason: Yup.string(),
  });
  
  const { hasFeedback, storeFeedback } = useStoredFeedback(articleId);
  const { vote, setVote } = useStoredVotes(articleId);

  const foregroundColor = backgroundColor === 'light' ? 'dark' : 'light';

  const submitForm = async (values: IFeedbackFormValues) => {
    // identify the user
    await submitFeedback({ category, locale, type: pageType, values, vote });
    storeFeedback();
  };

  const castVote = useCallback(async (action: 'downvote' | 'upvote') => {
    try {
      if (vote) return;

      if (action === 'downvote') {
        setVote(-1);
      } else {
        setVote(1);
      }

      if (pageType === 'devPortal') return;

      if (pageType === 'marketing') {
        await submitFeedback({ category, locale, type: pageType, vote });
        return;
      }

      // don't actually POST in storybook
      if (articleId === '123456') return;

      const response = await fetch(PUBLIC_GRAPHQL_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: `mutation { ${action}(articleId: "${articleId}", locale: "${locale}") { id }}`,
        }),
      });

      return response;
    } catch (error) {
      console.error('Error submitting vote: ', error);
      return false;
    }
  }, [articleId, vote, setVote, locale, pageType, category]);

  if (EXCLUDED_ARTICLES.includes(articleId)) return null;

  return (
    <div className={generateClassName([styles.articleVoteForm, hasFeedback && styles.hasFeedback, styles[backgroundColor], className])} {...props}>
      {(hasFeedback) ? (
          <div className={generateClassName([styles.headline, 'h6', 'semibold'])}>
            <Icon
              color='currentColor'
              iconName='CheckMark'
              size={3}
            />
            {t('molecules_articleVoteForm_success')}
          </div>
      ) : (
        <>
          <div className={generateClassName([styles.headline, 'h6', 'semibold'])}>
            {t('molecules_articleVoteForm_headline')}
          </div>
          {vote === 0 && (
            <div className={styles.actions}>
              <Button action={() => castVote('upvote')} className={styles.button} buttonColor={foregroundColor} variant="ghost">
                <>
                  <Icon fillColor='currentColor' iconName='Check' />
                  <span>{t('molecules_articleVoteForm_yes')}</span>
                </>
              </Button>
              <Button action={() => castVote('downvote')} className={styles.button} buttonColor={foregroundColor} variant="ghost">
                <>
                  <Icon fillColor='currentColor' iconName='X' />
                  <span>{t('molecules_articleVoteForm_no')}</span>
                </>
              </Button>
            </div>
          )}
          {vote !== 0 && (
            <Formik
              validationSchema={VALIDATION_SCHEMA}
              initialValues={INITIAL_VALUES}
              onSubmit={(values) => submitForm(values)}
            >
              {({ values }) => (
                <Form>
                  {vote === 1 && (
                    <div className={generateClassName([styles.feedbackThanks, 'p2'])}>
                      {t('molecules_articleVoteForm_thanks')}
                    </div>
                  )}
                  {vote === -1 && (
                    <RadioGroup
                      dataTestId='article-vote-form-reason-radio-group'
                      legend={t('molecules_articleVoteForm_form_legend')}
                      name="reason"
                      backgroundColor={backgroundColor}
                      options={[
                        { label: t('molecules_articleVoteForm_form_unclearLabel'), value: 'unclear' },
                        { label: t('molecules_articleVoteForm_form_wrongLabel'), value: 'wrong' },
                        { label: t('molecules_articleVoteForm_form_incompleteLabel'), value: 'incomplete' },
                        { label: t('molecules_articleVoteForm_form_irrelevantLabel'), value: 'irrelevant' },
                      ]}
                    />
                  )}
                  <TextareaInput
                    data-testid='article-vote-form-improvements-textarea'
                    label={vote === -1 ?  t('molecules_articleVoteForm_form_improveArticle') : t('molecules_articleVoteForm_form_moreDetail') }
                    name="improvements"
                    placeholder={vote === -1 ? t('molecules_articleVoteForm_form_describe') : t('molecules_articleVoteForm_form_improveArticleSuggestion')}
                    backgroundColor={backgroundColor}
                  />
                  <div className={styles.checkboxContainer}>
                    <CheckboxInput
                      dataTestId='article-vote-form-allow-contact-checkbox'
                      id="allowContact"
                      label={t('molecules_articleVoteForm_form_checkBoxLabel')}
                      name="allowContact"
                      backgroundColor={backgroundColor}
                    />
                  </div>
                  {values.allowContact && (
                    <TextInput
                      data-testid='article-vote-form-email-input'
                      label={t('molecules_articleVoteForm_form_textInput_label')}
                      description={t('molecules_articleVoteForm_form_textInput_description')}
                      name="email"
                      type="email"
                      placeholder={t('molecules_articleVoteForm_form_textInput_placeholder')}
                      backgroundColor={backgroundColor}
                    />
                  )}
                  <Button buttonColor={foregroundColor} type="submit" text={t('molecules_articleVoteForm_form_submit')}/>
                </Form>
              ) as ReactNode}
            </Formik>
          )}
          <div className='p2'>
            {t('molecules_articleVoteForm_furtherAssistance')} <a href={`https://help.klaviyo.com/hc/${t('langCodes_localeLowerCase')}/articles/115001002272`} target="_blank">{t('molecules_articleVoteForm_contactSupport')}</a>.
          </div>
        </>
      )}
    </div>
  );
};

export default ArticleVoteForm;
